import type { Environment } from '@leagueplatform/core';

import {
  TEST_ENVS,
  TEST_ENV_KEYS,
  type EosEnvironmentKey,
} from './environment-keys';

export type { EosEnvironmentKey } from './environment-keys';

export const EOS_ACTIVE_ENV_STORAGE_KEY = 'eos-active-environment';
export const EOS_MIRRORD_ROUTE_STORAGE_KEY = 'eos-mirrord-route';

export type EosEnvironmentDefinition = {
  key: EosEnvironmentKey;
  label: string;
  appEnvironment: Environment;
  apiUrl: string;
  wsApiUrl: string;
  legacyRestApiUrl: string;
  contentUrl: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  /** Auth0 DB connection for the reference tenant login, members and caregivers alike. */
  auth0Connection?: string;
};

type EnvEntryFromFile = {
  name: string;
  apiUrl: string;
};

const STAGING_DEFAULTS = {
  apiUrl: 'https://api.staging.league.dev',
  wsApiUrl: 'wss://api.staging.league.dev/ws',
  legacyRestApiUrl: 'https://rest.staging.league.dev',
  contentUrl: 'https://content.staging.league.dev',
  auth0Domain: 'accounts.reference.staging.league.dev',
  auth0ClientId: 'yzimJayVR2voAk7StVIxrPTJtDGLmbBU',
  auth0Audience: 'https://core-api.staging.league.dev',
  auth0Connection: 'Member-DB',
} as const;

const PROD_DEFAULTS = {
  apiUrl: 'https://api.league.com',
  wsApiUrl: 'wss://api.league.com/ws',
  legacyRestApiUrl: 'https://rest.league.com',
  contentUrl: 'https://content.league.com',
  auth0Domain: 'accounts.reference.league.com',
  auth0ClientId: 'zvAAeSPlSbRgeCViaqIEfbRRoflpGiac',
  auth0Audience: 'https://core-api.league.com',
  auth0Connection: 'Member-DB',
} as const;

const TEST_AUTH0_DOMAIN = 'accounts.reference.test.league.dev';
const TEST_AUTH0_CLIENT_ID = 'xKD6ApQnTSJfflKyHMN8t3Bx8lV8PRwQ';

/** Removes a trailing region suffix (e.g. "test2-ca" → "test2"). */
export const stripRegion = (envName: string): string => {
  const suffixes = ['-ca', '-au', '-us'];
  const suffix = suffixes.find((s) => envName.endsWith(s));
  return suffix ? envName.slice(0, -suffix.length) : envName;
};

const buildTestEnvironment = (envName: string): EosEnvironmentDefinition => {
  const slug = stripRegion(envName);
  const num = slug.replace('test', '');
  // The env's published domain (environment-keys.ts `audienceDomain`) is the single
  // source of truth. Every host derives from it, so the per-env subdomain scheme —
  // test2.league.dev (no region) / test5-ca.league.dev (hyphenated) /
  // test6.ca.league.dev (dotted) — is never re-guessed from the env key.
  const audienceDomain =
    TEST_ENVS.find((e) => e.key === envName)?.audienceDomain ??
    `${slug}.league.dev`;

  return {
    key: envName as EosEnvironmentKey,
    label: `Test ${num}`,
    appEnvironment: 'development',
    apiUrl: `https://api.${audienceDomain}`,
    wsApiUrl: `wss://api.${audienceDomain}/ws`,
    legacyRestApiUrl: `https://rest.${audienceDomain}`,
    contentUrl: `https://content.${audienceDomain}`,
    auth0Domain: TEST_AUTH0_DOMAIN,
    auth0ClientId: TEST_AUTH0_CLIENT_ID,
    auth0Audience: `https://core-api.${audienceDomain}`,
    auth0Connection: 'Member-DB',
  };
};

const TEST_ENV_DEFINITIONS = Object.fromEntries(
  TEST_ENV_KEYS.map((key) => [key, buildTestEnvironment(key)]),
) as Record<(typeof TEST_ENV_KEYS)[number], EosEnvironmentDefinition>;

const ENVIRONMENT_DEFINITIONS: Record<
  EosEnvironmentKey,
  EosEnvironmentDefinition
> = {
  '': {
    key: '',
    label: 'Staging',
    appEnvironment: 'development',
    ...STAGING_DEFAULTS,
  },
  staging: {
    key: 'staging',
    label: 'Staging',
    appEnvironment: 'development',
    ...STAGING_DEFAULTS,
  },
  prod: {
    key: 'prod',
    label: 'Production',
    appEnvironment: 'production',
    ...PROD_DEFAULTS,
  },
  ...TEST_ENV_DEFINITIONS,
};

const SWITCHER_ENV_KEYS: EosEnvironmentKey[] = ['', ...TEST_ENV_KEYS, 'prod'];

const parseEnvironmentsFromEnv = (): EnvEntryFromFile[] => {
  const raw = import.meta.env.VITE_ENVIRONMENTS;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as EnvEntryFromFile[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeEnvironmentKey = (
  value: string | null | undefined,
): EosEnvironmentKey => {
  if (!value) return '';

  if (value === 'staging' || value === 'stage') return '';
  if (value === 'prod' || value === 'production') return 'prod';

  const knownKeys = Object.keys(ENVIRONMENT_DEFINITIONS) as EosEnvironmentKey[];
  if (knownKeys.includes(value as EosEnvironmentKey)) {
    return value as EosEnvironmentKey;
  }

  return '';
};

const getBuildTimeEnvironmentKey = (): EosEnvironmentKey => {
  if (import.meta.env.VITE_ACTIVE_ENV) {
    return normalizeEnvironmentKey(import.meta.env.VITE_ACTIVE_ENV);
  }

  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  if (apiUrl.includes('api.league.com')) {
    return 'prod';
  }

  // Matches all three published subdomain schemes: api.test2.league.dev (no region),
  // api.test5-ca.league.dev (hyphenated), api.test6.ca.league.dev (dotted).
  const testMatch = apiUrl.match(
    /api\.(test\d+)(?:[.-][a-z]{2,3})?\.league\.dev/,
  );
  if (testMatch) {
    const testKey = `${testMatch[1]}-ca` as EosEnvironmentKey;
    if (testKey in ENVIRONMENT_DEFINITIONS) {
      return testKey;
    }
  }

  if (import.meta.env.VITE_APP_ENV === 'production') {
    return 'prod';
  }

  return '';
};

export const getPersistedEnvironmentKey = (): EosEnvironmentKey => {
  if (typeof window === 'undefined') {
    return getBuildTimeEnvironmentKey();
  }

  const stored = localStorage.getItem(EOS_ACTIVE_ENV_STORAGE_KEY);
  if (stored !== null) {
    return normalizeEnvironmentKey(stored);
  }

  return getBuildTimeEnvironmentKey();
};

export const persistEnvironmentKey = (key: EosEnvironmentKey): void => {
  localStorage.setItem(EOS_ACTIVE_ENV_STORAGE_KEY, key);
};

const getBuildTimeMirrordRoute = (): string =>
  (import.meta.env.VITE_MIRRORD_ROUTE ?? '').trim();

export const getPersistedMirrordRoute = (): string => {
  if (typeof window === 'undefined') {
    return getBuildTimeMirrordRoute();
  }

  const stored = localStorage.getItem(EOS_MIRRORD_ROUTE_STORAGE_KEY);
  if (stored !== null) {
    return stored.trim();
  }

  return getBuildTimeMirrordRoute();
};

export const persistMirrordRoute = (route: string): void => {
  localStorage.setItem(EOS_MIRRORD_ROUTE_STORAGE_KEY, route.trim());
};

export const getEnvironmentDefinition = (
  key: EosEnvironmentKey,
): EosEnvironmentDefinition => {
  if (key in ENVIRONMENT_DEFINITIONS) {
    return ENVIRONMENT_DEFINITIONS[key];
  }

  const fromEnvFile = parseEnvironmentsFromEnv().find(
    (entry) => entry.name === key,
  );
  if (fromEnvFile) {
    return buildTestEnvironment(fromEnvFile.name);
  }

  return ENVIRONMENT_DEFINITIONS[''];
};

export const getSwitcherEnvironments = (): EosEnvironmentDefinition[] =>
  SWITCHER_ENV_KEYS.map((key) => getEnvironmentDefinition(key));
